import React, { lazy } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import cleenDeep from 'clean-deep';
import Dialog from '@mui/material/Dialog';
import styled from '@emotion/styled';
import storage from 'helpers/storage';
import parseQS from 'helpers/parseQS';
import { createOnboarding, updateOnboarding } from 'actions/onboarding';
import { getLocalizedPath } from 'helpers/getLocale';
import Start from './steps/Start';
// Vite serves SVGs through the `?react` query (vite-plugin-svgr) where the
// default export IS the React component. The previous form
//   import('…svg').then(m => ({ default: m.ReactComponent }))
// was a CRA convention and resolved to { default: undefined } under Vite,
// which crashed the fullscreen dialog (lazy element type must resolve to
// a function — it got undefined).
const Stepper = lazy(() => import('./components/Stepper'));
const KnowWhatTodo = lazy(() => import('./steps/KnowWhatTodo'));
const TimePerDay = lazy(() => import('./steps/TimePerDay'));
const InformationType = lazy(() => import('./steps/InformationType'));
const NeedSupport = lazy(() => import('./steps/NeedSupport'));
const Notifications = lazy(() => import('./steps/Notifications'));
const SpheresStep = lazy(() => import('./steps/Spheres'));
const IntroStep = lazy(() => import('./steps/Intro'));
const GenderStep = lazy(() => import('./steps/GenderChoose'));
const NameStep = lazy(() => import('./steps/NameChoose'));
const GreetingsStep = lazy(() => import('./steps/Greetings'));
const AgeStep = lazy(() => import('./steps/AgeChoose'));
const Benefits = lazy(() => import('./steps/Benefits'));
const StrengthsStep = lazy(() => import('./steps/Strengths'));
const GetTrollarsStep = lazy(() => import('./steps/GetTrollars'));
const DoingMyJubStep = lazy(() => import('./steps/DoingMyJub'));
const SubscribeType = lazy(() => import('./steps/SubscribeType'));
const RegistrationStep = lazy(() => import('./steps/Registration'));

const ContentBackground = styled('section')(({ theme }) => ({
  borderBottom: `3px solid ${theme.palette.primary.main}`
}));

const Content = styled('div')(({ theme }) => ({
  padding: '40px 100px',
  ...theme.wrapper,
  [theme.breakpoints.down('lg')]: {
    padding: 50
  },
  [theme.breakpoints.down('md')]: {
    padding: 30
  },
  [theme.breakpoints.down('sm')]: {
    padding: 20
  }
}));

const Wrapper = styled('div')(() => ({
  marginBottom: 20
}));

const WrapperDialog = styled('div')(({ theme, maxWidth, width }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flex: 4,
  maxWidth: maxWidth || 820,
  width: width || '100%',
  gap: 20,
  [theme.breakpoints.down('lg')]: {
    display: 'block',
    maxWidth: '100%',
    width: '100%'
  }
}));

const DialogStyled = styled(Dialog)(() => ({
  '& .MuiDialog-paper': {
    alignItems: 'center'
  }
}));

const MaxWidth = styled('div')(({ theme, fullHeight, fullHeightSm }) => ({
  width: 820,
  borderRadius: 0,
  height: fullHeight === 'true' ? 'unset' : '100vh',
  marginTop: fullHeight === 'true' ? 20 : 0,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-around',
  alignItems: 'center',
  [theme.breakpoints.down('md')]: {
    width: '100%',
    height: '100%'
  },
  [theme.breakpoints.down('sm')]: {
    paddingTop: 20,
    height: fullHeightSm === 'true' ? 'unset' : '100vh'
  }
}));

const TOTAL_STEPS = 17;
const ONBOARDING_STEPS = Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1);

// Index → URL slug. Step 0 = the landing/Start screen (dialog closed).
// Steps 1..17 = each fullscreen-dialog screen. Adding or reordering steps
// here requires keeping STEP_INDEX_BY_SLUG (derived below) in sync.
const STEP_SLUGS = [
  'start',              // 0 — landing
  'intro',              // 1
  'name',               // 2
  'greetings',          // 3
  'know-what-todo',     // 4
  'gender',             // 5
  'strengths',          // 6
  'benefits',           // 7
  'age',                // 8
  'trollars',           // 9
  'time-per-day',       // 10
  'information-type',   // 11
  'need-support',       // 12
  'notifications',      // 13
  'my-job',             // 14
  'spheres',            // 15
  'subscribe',          // 16
  'register'            // 17
];
const STEP_INDEX_BY_SLUG = STEP_SLUGS.reduce((acc, s, i) => ({ ...acc, [s]: i }), {});

const Onboarding = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { step: stepSlug } = useParams();
  // Active step is derived from the URL. When the Onboarding component is
  // embedded on Home (where there's no :step param), it falls back to step 0
  // (Start landing). The Dialog is "open" whenever activeStep > 0.
  const activeStep = React.useMemo(() => {
    if (!stepSlug) return 0;
    const idx = STEP_INDEX_BY_SLUG[stepSlug];
    return idx === undefined ? 0 : idx;
  }, [stepSlug]);
  const open = activeStep > 0;
  const [highlightStep, setHighlightStep] = React.useState(0);
  const [onboardingData, setOnboardingData] = React.useState(() => {
    const onboarding = storage.getItem('onboarding');
    const parsed = JSON.parse(onboarding);
    return onboarding
      ? cleenDeep({
          ...parsed,
          age: parsed?.birthdate ? dayjs(parsed?.birthdate).format('DD.MM.YYYY') : null
        })
      : {};
  });
  const [onboardingId, setOnboardingId] = React.useState(null);
  const [steps] = React.useState(ONBOARDING_STEPS);

  const handleCreateOnboarding = React.useCallback(async () => {
    if (onboardingData._id) {
      setOnboardingId(onboardingData._id);
      return;
    }
    const result = await dispatch(createOnboarding());
    setOnboardingId(result._id);
  }, [onboardingData, dispatch]);

  const handleUpdateOnboarding = React.useCallback(async () => {
    const body = cleenDeep({
      gender: onboardingData?.gender,
      name: onboardingData?.name,
      confidenceInProfession: onboardingData?.confidenceInProfession,
      spendTimeOpportunity: onboardingData?.spendTimeOpportunity,
      informationPerception: onboardingData?.informationPerception,
      needSupport: onboardingData?.needSupport,
      spheres: onboardingData?.spheres,
      strengths: onboardingData?.strengths,
      birthdate: onboardingData?.age
        ? dayjs(onboardingData?.age, 'DD.MM.YYYY').format('YYYY-MM-DD')
        : null
    });

    if (Object.keys(body).length === 0) {
      return;
    }

    const result = await dispatch(updateOnboarding(onboardingId, body));
    storage.setItem('onboarding', JSON.stringify(result));
  }, [dispatch, onboardingData, onboardingId]);

  // All step transitions go through the URL so each onboarding screen has
  // a unique address that's bookmarkable + back-button-safe. The local
  // `highlightStep` (used by Stepper for active-section highlight) is
  // derived from activeStep — kept as state so a useEffect can sync it.
  const goToStep = React.useCallback(
    (idx) => {
      const slug = STEP_SLUGS[idx];
      if (!slug) return;
      navigate(getLocalizedPath(`/onboarding/${slug}`));
    },
    [navigate]
  );

  const handleStop = React.useCallback(() => {
    goToStep(0);
  }, [goToStep]);

  const handleIncrementStep = React.useCallback(() => {
    const next = activeStep + 1;
    if (next > TOTAL_STEPS) return;
    goToStep(next);
  }, [activeStep, goToStep]);

  const handleOpenFullScreen = React.useCallback(() => {
    goToStep(1);
  }, [goToStep]);

  // Keep highlightStep in sync with activeStep so Stepper highlight updates
  // when the URL changes (back / forward / direct nav).
  React.useEffect(() => {
    if (activeStep >= 1 && activeStep <= 12) {
      setHighlightStep(Math.ceil(activeStep / 2));
    } else {
      setHighlightStep(0);
    }
  }, [activeStep]);

  const FullScreenWrapper = React.useCallback(
    ({ children, maxWidth, width, fullHeight, fullHeightSm }) => (
      <DialogStyled fullScreen={true} open={open} transitionDuration={0}>
        <MaxWidth fullHeightSm={fullHeightSm} fullHeight={`${fullHeight}`}>
          <WrapperDialog width={width} maxWidth={maxWidth}>
            {children}
          </WrapperDialog>

          <Stepper
            handleStop={handleStop}
            steps={steps.slice(0, Math.ceil(steps.length / 2))}
            activeStep={activeStep}
            highlightStep={highlightStep}
            setStep={goToStep}
          />
        </MaxWidth>
      </DialogStyled>
    ),
    [open, steps, activeStep, handleStop, highlightStep, goToStep]
  );

  React.useEffect(() => {
    if (onboardingId) {
      handleUpdateOnboarding();
    }
  }, [handleUpdateOnboarding, onboardingId]);

  // Legacy support: `?step=N` (numeric) → redirect to the slug URL so
  // bookmarks/links from before the refactor still work.
  React.useEffect(() => {
    const { step } = parseQS(location.search);
    if (!step) return;
    const idx = Number(step);
    const slug = STEP_SLUGS[idx];
    if (!slug) return;
    navigate(getLocalizedPath(`/onboarding/${slug}`), { replace: true });
  }, [location, navigate]);

  React.useEffect(() => {
    const mainLayout = document.getElementById('root');
    mainLayout.style.opacity = open ? 0 : 1;
    return () => {
      mainLayout.style.opacity = 1;
    };
  }, [open]);

  return (
    <>
      <ContentBackground>
        <Content>
          <Wrapper>
            {activeStep === 0 ? <Start handleOpen={handleOpenFullScreen} /> : null}

            {activeStep === 1 ? (
              <FullScreenWrapper>
                <IntroStep
                  handleIncrementStep={handleIncrementStep}
                  handleCreateOnboarding={handleCreateOnboarding}
                />
              </FullScreenWrapper>
            ) : null}

            {activeStep === 2 ? (
              <FullScreenWrapper>
                <NameStep
                  handleIncrementStep={handleIncrementStep}
                  onboardingData={onboardingData}
                  setValue={setOnboardingData}
                />
              </FullScreenWrapper>
            ) : null}

            {activeStep === 3 ? (
              <FullScreenWrapper>
                <GreetingsStep
                  handleIncrementStep={handleIncrementStep}
                  onboardingData={onboardingData}
                />
              </FullScreenWrapper>
            ) : null}

            {activeStep === 4 ? (
              <FullScreenWrapper>
                <KnowWhatTodo
                  onboardingData={onboardingData}
                  setValue={setOnboardingData}
                  handleIncrementStep={handleIncrementStep}
                />
              </FullScreenWrapper>
            ) : null}

            {activeStep === 5 ? (
              <FullScreenWrapper>
                <GenderStep
                  handleIncrementStep={handleIncrementStep}
                  handleCreateOnboarding={handleCreateOnboarding}
                  onboardingData={onboardingData}
                  setValue={setOnboardingData}
                />
              </FullScreenWrapper>
            ) : null}

            {activeStep === 6 ? (
              <FullScreenWrapper>
                <StrengthsStep
                  handleIncrementStep={handleIncrementStep}
                  handleCreateOnboarding={handleCreateOnboarding}
                  onboardingData={onboardingData}
                  setValue={setOnboardingData}
                />
              </FullScreenWrapper>
            ) : null}

            {activeStep === 7 ? (
              <FullScreenWrapper fullHeightSm={'true'}>
                <Benefits handleIncrementStep={handleIncrementStep} />
              </FullScreenWrapper>
            ) : null}

            {activeStep === 8 ? (
              <FullScreenWrapper>
                <AgeStep
                  fullHeightSm={'true'}
                  handleIncrementStep={handleIncrementStep}
                  onboardingData={onboardingData}
                  setValue={setOnboardingData}
                />
              </FullScreenWrapper>
            ) : null}

            {activeStep === 9 ? (
              <FullScreenWrapper>
                <GetTrollarsStep handleIncrementStep={handleIncrementStep} />
              </FullScreenWrapper>
            ) : null}

            {activeStep === 10 ? (
              <FullScreenWrapper>
                <TimePerDay
                  onboardingData={onboardingData}
                  setValue={setOnboardingData}
                  handleIncrementStep={handleIncrementStep}
                />
              </FullScreenWrapper>
            ) : null}

            {activeStep === 11 ? (
              <FullScreenWrapper>
                <InformationType
                  onboardingData={onboardingData}
                  setValue={setOnboardingData}
                  handleIncrementStep={handleIncrementStep}
                />
              </FullScreenWrapper>
            ) : null}

            {activeStep === 12 ? (
              <FullScreenWrapper>
                <NeedSupport
                  onboardingData={onboardingData}
                  setValue={setOnboardingData}
                  handleIncrementStep={handleIncrementStep}
                />
              </FullScreenWrapper>
            ) : null}

            {activeStep === 13 ? (
              <FullScreenWrapper>
                <Notifications
                  onboardingData={onboardingData}
                  setValue={setOnboardingData}
                  handleIncrementStep={handleIncrementStep}
                />
              </FullScreenWrapper>
            ) : null}

            {activeStep === 14 ? (
              <FullScreenWrapper>
                <DoingMyJubStep handleIncrementStep={handleIncrementStep} />
              </FullScreenWrapper>
            ) : null}

            {activeStep === 15 ? (
              <FullScreenWrapper>
                <SpheresStep
                  onboardingData={onboardingData}
                  setValue={setOnboardingData}
                  handleIncrementStep={handleIncrementStep}
                />
              </FullScreenWrapper>
            ) : null}

            {activeStep === 16 ? (
              <FullScreenWrapper fullHeight={true} width={'1200px'} maxWidth={'1200px'}>
                <SubscribeType handleIncrementStep={handleIncrementStep} />
              </FullScreenWrapper>
            ) : null}

            {activeStep === 17 ? (
              <FullScreenWrapper>
                <RegistrationStep
                  handleIncrementStep={handleIncrementStep}
                  handleCreateOnboarding={handleCreateOnboarding}
                />
              </FullScreenWrapper>
            ) : null}
          </Wrapper>
        </Content>
      </ContentBackground>
    </>
  );
};

export default Onboarding;
